import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { HiPlus, HiPencil, HiTrash, HiPlay } from 'react-icons/hi2';
import type { RootState } from '@/store/store';
import type { PortalCredentialDto, CreatePortalCredentialRequest, UpdatePortalCredentialRequest } from '@/types';
import { isApiError } from '@/utils/errorUtils';
import { useDateFormat } from '@/utils/dates';
import { PageContent, PageHeader } from '@/components/layout/PageLayout';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import {
  EmptyState, EmptyStateIcon, EmptyStateTitle, EmptyStateDescription,
  DesktopOnly, MobileOnly, MobileCardList, MobileCard, MobileCardHeader, MobileCardBody, MobileCardActions, MobileCardMain, MobileCardTitle, LoadingCenter,
} from '@/styles/shared.styled';
import {
  useListPortalCredentialsQuery,
  useCreatePortalCredentialMutation,
  useUpdatePortalCredentialMutation,
  useDeletePortalCredentialMutation,
  useValidatePortalCredentialMutation,
} from '@/store/portalCredentialsApi';

export default function PortalCredentialsPage() {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const { user, isLoading: isAuthLoading } = useSelector((state: RootState) => state.auth);
  const isAdmin = user?.permissions?.includes('admin') ?? false;

  const { data, isLoading } = useListPortalCredentialsQuery(undefined, {
    skip: !isAdmin,
  });
  const hasPending = useMemo(
    () => data?.items.some((c) => c.validationStatus === 'Pending') ?? false,
    [data],
  );
  // Poll only when there are pending validations
  useListPortalCredentialsQuery(undefined, {
    skip: !isAdmin || !hasPending,
    pollingInterval: 5000,
  });
  const [createCredential, { isLoading: isCreating }] = useCreatePortalCredentialMutation();
  const [updateCredential] = useUpdatePortalCredentialMutation();
  const [deleteCredential] = useDeletePortalCredentialMutation();
  const [validateCredential] = useValidatePortalCredentialMutation();

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PortalCredentialDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PortalCredentialDto | null>(null);
  const [form, setForm] = useState<{
    login: string;
    password: string;
    isActive: boolean;
  }>({
    login: '',
    password: '',
    isActive: true,
  });

  if (isAuthLoading) {
    return (
      <PageContent>
        <LoadingCenter>
          <Spinner />
        </LoadingCenter>
      </PageContent>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/sprawy" replace />;
  }

  const openCreate = () => {
    setEditing(null);
    setForm({ login: '', password: '', isActive: true });

    setShowModal(true);
  };

  const openEdit = (cred: PortalCredentialDto) => {
    setEditing(cred);
    setForm({ login: '', password: '', isActive: cred.isActive });

    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let result;
    if (editing) {
      const body: UpdatePortalCredentialRequest & { id: string } = {
        id: editing.id,
        login: form.login || undefined,
        password: form.password || undefined,
        isActive: form.isActive,
      };
      result = await updateCredential(body);
    } else {
      const body: CreatePortalCredentialRequest = {
        login: form.login,
        password: form.password,
      };
      result = await createCredential(body);
    }

    if (!isApiError(result)) {
      setShowModal(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteCredential(deleteTarget.id);
    setDeleteTarget(null);
  };

  const formatDate = (dateStr: string | null) => (dateStr ? formatDateTime(dateStr) : '—');

  const renderCredentialsBadge = (cred: PortalCredentialDto) => {
    if (!cred.hasCredentials) {
      return <Badge bg="secondary">{t('portalCredentials.notConfigured', 'Not configured')}</Badge>;
    }
    if (cred.validationStatus === 'Invalid') {
      return (
        <Badge bg="danger" title={cred.validationError ?? ''}>
          {t('portalCredentials.invalidCredentials', 'Invalid credentials')}
        </Badge>
      );
    }
    if (cred.validationStatus === 'Valid') {
      return <Badge bg="success">{t('portalCredentials.configured', 'Configured')}</Badge>;
    }
    return <Badge bg="info">{t('portalCredentials.configured', 'Configured')}</Badge>;
  };

  const credentialColumns: DataTableColumn<PortalCredentialDto>[] = [
    {
      key: 'credentials',
      header: t('portalCredentials.table.credentials', 'Credentials'),
      render: renderCredentialsBadge,
    },
    {
      key: 'status',
      header: t('portalCredentials.table.status', 'Status'),
      render: (cred) => (
        <>
          <Badge bg={cred.isActive ? 'success' : 'secondary'}>
            {cred.isActive ? t('portalCredentials.active', 'Active') : t('portalCredentials.inactive', 'Inactive')}
          </Badge>
          {cred.lastSyncError && (
            <Badge bg="danger" className="ms-1" title={cred.lastSyncError}>
              {t('portalCredentials.error', 'Error')}
            </Badge>
          )}
          {cred.validationStatus === 'Pending' && (
            <Badge bg="warning" className="ms-1">
              <Spinner animation="border" size="sm" /> {t('portalCredentials.validating')}
            </Badge>
          )}
          {cred.validationStatus === 'Valid' && (
            <Badge bg="success" className="ms-1">{t('portalCredentials.valid')}</Badge>
          )}
          {cred.validationStatus === 'Invalid' && (
            <Badge bg="danger" className="ms-1" title={cred.validationError ?? ''}>
              {t('portalCredentials.invalid')}
            </Badge>
          )}
        </>
      ),
    },
    {
      key: 'lastSync',
      header: t('portalCredentials.table.lastSync', 'Last Sync'),
      render: (cred) => <small>{formatDate(cred.lastSyncAt)}</small>,
    },
    {
      key: 'actions',
      header: t('portalCredentials.table.actions', 'Actions'),
      width: '140px',
      render: (cred) => (
        <div className="d-flex gap-1">
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => validateCredential(cred.id)}
            disabled={cred.validationStatus === 'Pending'}
          >
            <HiPlay />
          </Button>
          <Button variant="outline-primary" size="sm" onClick={() => openEdit(cred)}>
            <HiPencil />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(cred)}>
            <HiTrash />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageContent>
      <PageHeader
        title={t('portalCredentials.title', 'Portal Credentials')}
        subtitle={t('portalCredentials.subtitle', 'Manage court portal login credentials for automatic case synchronization')}
        actions={
          <Button variant="primary" onClick={openCreate}>
            <HiPlus className="me-1" />
            {t('portalCredentials.add', 'Add Credential')}
          </Button>
        }
      />

      {isLoading && (
        <div className="text-center py-4">
          <Spinner />
        </div>
      )}

      
      {data && data.items.length > 0 && (
        <>
          <DesktopOnly>
            <DataTable<PortalCredentialDto>
              ariaLabel={t('portalCredentials.title', 'Portal Credentials')}
              rowKey={(c) => c.id}
              columns={credentialColumns}
              rows={data.items}
            />
          </DesktopOnly>

          <MobileOnly>
            <MobileCardList>
              {data.items.map((cred) => (
                <MobileCard key={cred.id}>
                  <MobileCardHeader>
                    <MobileCardMain>
                      <MobileCardTitle>{renderCredentialsBadge(cred)}</MobileCardTitle>
                    </MobileCardMain>
                  </MobileCardHeader>
                  <MobileCardBody>
                    <Badge bg={cred.isActive ? 'success' : 'secondary'}>
                      {cred.isActive ? t('portalCredentials.active', 'Active') : t('portalCredentials.inactive', 'Inactive')}
                    </Badge>
                    {cred.validationStatus === 'Valid' && <Badge bg="success">{t('portalCredentials.valid')}</Badge>}
                    {cred.validationStatus === 'Invalid' && <Badge bg="danger">{t('portalCredentials.invalid')}</Badge>}
                    {cred.validationStatus === 'Pending' && <Badge bg="warning">{t('portalCredentials.validating')}</Badge>}
                    {cred.lastSyncError && <Badge bg="danger">{t('portalCredentials.error', 'Error')}</Badge>}
                    <span>{formatDate(cred.lastSyncAt)}</span>
                  </MobileCardBody>
                  <MobileCardActions>
                    <Button variant="outline-success" size="sm" onClick={() => validateCredential(cred.id)} disabled={cred.validationStatus === 'Pending'}>
                      <HiPlay size="0.875rem" />
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => openEdit(cred)}>
                      <HiPencil size="0.875rem" />
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(cred)}>
                      <HiTrash size="0.875rem" />
                    </Button>
                  </MobileCardActions>
                </MobileCard>
              ))}
            </MobileCardList>
          </MobileOnly>
        </>
      )}

      {data && data.items.length === 0 && (
        <EmptyState>
          <EmptyStateIcon><HiPlus /></EmptyStateIcon>
          <EmptyStateTitle>{t('portalCredentials.empty', 'No portal credentials configured.')}</EmptyStateTitle>
          <EmptyStateDescription>{t('portalCredentials.emptyDesc', 'Add one to start syncing cases from the court portal.')}</EmptyStateDescription>
        </EmptyState>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editing
                ? t('portalCredentials.editTitle', 'Edit Credential')
                : t('portalCredentials.addTitle', 'Add Credential')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                {t('portalCredentials.form.login', 'Login (PESEL)')}
                {editing && <small className="text-muted ms-1">({t('portalCredentials.form.leaveEmpty', 'leave empty to keep current')})</small>}
              </Form.Label>
              <Form.Control
                type="text"
                value={form.login}
                onChange={(e) => setForm({ ...form, login: e.target.value })}
                placeholder={t('portalCredentials.form.loginPlaceholder', 'e.g. 62012345678')}
                required={!editing}
                maxLength={11}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                {t('portalCredentials.form.password', 'Password')}
                {editing && <small className="text-muted ms-1">({t('portalCredentials.form.leaveEmpty', 'leave empty to keep current')})</small>}
              </Form.Label>
              <Form.Control
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!editing}
              />
            </Form.Group>
            {editing && (
              <Form.Check
                type="switch"
                label={t('portalCredentials.form.active', 'Active')}
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
            )}

          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={isCreating}>
              {isCreating ? <Spinner size="sm" className="me-2" /> : null}
              {editing ? t('common.save', 'Save') : t('portalCredentials.add', 'Add Credential')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('portalCredentials.deleteTitle', 'Delete Credential')}
        message={t('portalCredentials.deleteConfirm', 'Are you sure you want to delete this credential? This will also remove the stored login and password.')}
        variant="danger"
        confirmLabel={t('confirm.delete', 'Delete')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageContent>
  );
}
